// Bulk record import dialogs — Clients (org-wide), Individuals (per-client), Entities (per-client).
// Mirrors the BulkTasksDialogs pattern: pick → preview → commit.

import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Loader2, Download, AlertCircle, CheckCircle2, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Generic bulk-import dialog. Configure with:
 *   templateUrl   - endpoint to GET the CSV template
 *   importUrl     - endpoint to POST the CSV (multipart)
 *   recordLabel   - "client" | "individual" | "entity"
 *   previewCols   - [{ key, label }] for the preview table
 *   testidPrefix  - data-testid prefix for the dialog and buttons
 */
function GenericBulkRecordDialog({
  open, onOpenChange, onImported,
  templateUrl, importUrl,
  recordLabel, recordLabelPlural,
  previewCols, testidPrefix,
}) {
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [step, setStep] = useState('pick');

  const reset = () => { setFile(null); setPreview(null); setStep('pick'); };

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API_BASE}${templateUrl}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast.error('Could not download template'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${recordLabelPlural}_template.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Network error'); }
  };

  const runPreview = async () => {
    if (!file) return;
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('dryRun', 'true');
      const res = await fetch(`${API_BASE}${importUrl}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : 'Could not read CSV');
        setPreviewing(false); return;
      }
      setPreview(await res.json()); setStep('preview');
    } catch { toast.error('Network error'); }
    setPreviewing(false);
  };

  const commitImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('dryRun', 'false');
      const res = await fetch(`${API_BASE}${importUrl}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : (err.detail?.message || 'Import failed'));
        setImporting(false); return;
      }
      const data = await res.json();
      toast.success(`${data.imported} ${data.imported === 1 ? recordLabel : recordLabelPlural} imported`);
      onImported?.();
      onOpenChange(false);
      setTimeout(reset, 250);
    } catch { toast.error('Network error'); }
    setImporting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setTimeout(reset, 250); }}>
      <DialogContent className="max-w-2xl" data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle>Bulk import {recordLabelPlural}</DialogTitle>
          <DialogDescription>
            Upload a CSV of {recordLabelPlural}. Use the template if you don't have one yet.
          </DialogDescription>
        </DialogHeader>

        {step === 'pick' && (
          <div className="space-y-4">
            <Button variant="outline" onClick={downloadTemplate} className="w-full gap-2" data-testid={`${testidPrefix}-download-template-btn`}>
              <Download className="w-4 h-4" /> Download CSV template
            </Button>
            <div className="border-2 border-dashed border-[#E6E0D9] rounded-lg p-6 text-center">
              <FileText className="w-8 h-8 text-[#A86A3A] mx-auto mb-3" />
              <input
                type="file" accept=".csv,text/csv" id={`${testidPrefix}-file`}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid={`${testidPrefix}-file-input`}
              />
              <label htmlFor={`${testidPrefix}-file`} className="cursor-pointer text-sm text-[#A86A3A] hover:underline">
                {file ? <span>Selected: <b>{file.name}</b></span> : 'Click to pick a .csv file'}
              </label>
            </div>
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
                <p className="text-[10px] uppercase tracking-wider text-emerald-700">Ready to import</p>
                <p className="text-2xl font-light text-emerald-900">{preview.validCount}</p>
              </div>
              <div className={`${preview.errorCount > 0 ? 'bg-red-50 border-red-200' : 'bg-stone-50 border-stone-200'} border rounded-md p-3`}>
                <p className="text-[10px] uppercase tracking-wider text-stone-700">Errors</p>
                <p className={`text-2xl font-light ${preview.errorCount > 0 ? 'text-red-700' : 'text-stone-700'}`}>{preview.errorCount}</p>
              </div>
            </div>

            {preview.errors?.length > 0 && (
              <div className="max-h-40 overflow-y-auto bg-red-50 border border-red-200 rounded-md p-3 space-y-1">
                {preview.errors.map((e, i) => (
                  <p key={i} className="text-xs text-red-800 flex items-start gap-1.5">
                    <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{e.error}</span>
                  </p>
                ))}
              </div>
            )}

            {preview.preview?.length > 0 && (
              <div className="max-h-48 overflow-y-auto border border-[#E6E0D9] rounded-md">
                <table className="w-full text-xs">
                  <thead className="bg-[#F9F7F5] text-[#5D524D]">
                    <tr>{previewCols.map(c => <th key={c.key} className="p-2 text-left">{c.label}</th>)}</tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((p, i) => (
                      <tr key={i} className="border-t border-[#E6E0D9]">
                        {previewCols.map(c => (
                          <td key={c.key} className="p-2">{p[c.key] || '—'}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'pick' && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={runPreview} disabled={!file || previewing} data-testid={`${testidPrefix}-preview-btn`}>
                {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Preview
              </Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={reset}>Back</Button>
              <Button
                onClick={commitImport}
                disabled={importing || preview?.errorCount > 0 || (preview?.validCount || 0) === 0}
                data-testid={`${testidPrefix}-commit-btn`}
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Import {preview?.validCount || 0} {(preview?.validCount || 0) === 1 ? recordLabel : recordLabelPlural}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============ Pre-configured exports ============

export function BulkClientsDialog({ open, onOpenChange, onImported }) {
  return (
    <GenericBulkRecordDialog
      open={open} onOpenChange={onOpenChange} onImported={onImported}
      templateUrl="/clients-bulk/template.csv"
      importUrl="/clients-bulk/import"
      recordLabel="client"
      recordLabelPlural="clients"
      previewCols={[
        { key: 'name', label: 'Name' },
        { key: 'segment', label: 'Segment' },
        { key: 'relationshipLead', label: 'Lead' },
      ]}
      testidPrefix="bulk-clients"
    />
  );
}

export function BulkIndividualsDialog({ open, onOpenChange, clientId, onImported }) {
  if (!clientId) return null;
  return (
    <GenericBulkRecordDialog
      open={open} onOpenChange={onOpenChange} onImported={onImported}
      templateUrl={`/clients/${clientId}/individuals-bulk/template.csv`}
      importUrl={`/clients/${clientId}/individuals-bulk/import`}
      recordLabel="individual"
      recordLabelPlural="individuals"
      previewCols={[
        { key: 'name', label: 'Name' },
        { key: 'role', label: 'Family role' },
        { key: 'generation', label: 'Gen' },
      ]}
      testidPrefix="bulk-individuals"
    />
  );
}

export function BulkEntitiesDialog({ open, onOpenChange, clientId, onImported }) {
  if (!clientId) return null;
  return (
    <GenericBulkRecordDialog
      open={open} onOpenChange={onOpenChange} onImported={onImported}
      templateUrl={`/clients/${clientId}/entities-bulk/template.csv`}
      importUrl={`/clients/${clientId}/entities-bulk/import`}
      recordLabel="entity"
      recordLabelPlural="entities"
      previewCols={[
        { key: 'name', label: 'Name' },
        { key: 'type', label: 'Type' },
        { key: 'abn', label: 'ABN' },
      ]}
      testidPrefix="bulk-entities"
    />
  );
}
